(ns frontend.view.nev.fundraising-opportunity.view
  (:require
   [clojure.string :as string]
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.field :as field]
   [frontend.actions.common :as common-actions]
   [frontend.db :as app-db]
   [frontend.domain.fundraising-opportunity.actions :as fundraising-opportunity-actions]
   [frontend.logging :as logging]
   [frontend.page-components.default-page-layout :as default-page-layout]
   [frontend.page-components.zap-toolbar :as zap-toolbar]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.state.permissions :as f-permissions]
   [frontend.tenant-customizations :as tenant-customizations]
   [frontend.view.entity-viewers.components :as nev-components]
   [frontend.view.entity-viewers.entity-editor :as entity-editor]
   [frontend.view.entity-viewers.event-pane :as event-pane]
   [frontend.view.entity-viewers.header :as header]
   [frontend.view.entity-viewers.modules :as modules]
   [frontend.view.entity-viewers.modules.associated-entities :as associated-entities]
   [frontend.view.entity-viewers.modules.check-list.view :as check-list-module]
   [frontend.view.entity-viewers.modules.fees :as fees]
   [frontend.view.entity-viewers.utils :as entity-utils]
   [frontend.view.field-templates :as templates]
   [frontend.view.modals.change-pipeline.view :as change-pipeline]
   [frontend.view.modals.curtains.fundraising-opportunity.lost-curtain :as loss-curtain]
   [frontend.view.modals.curtains.fundraising-opportunity.on-hold-curtain :as on-hold-curtain]
   [frontend.view.modals.events :as modals]
   [frontend.view.nev.events :as events]
   [frontend.view.not-found :as not-found]
   [frontend.view.syncing-data :as syncing]
   [reagent.core :as r]
   [ui.util :as uu]))

(defn toolbar-buttons
  [{:keys [entity] :as _view-args}]
  (let [entity-id (:db/id entity)
        flagged? (contains? (:account/flagged-entities @session/account)
                            entity-id)]
    (concat
     []
     (when-not (session/is-read-only-user?)
       [(zap-toolbar/dropdown-button
         {:icon      :fa-star
          :on-select (partial common-actions/toggle-flag entity flagged?)
          :ord       1
          :label     (loc/tr (if flagged? :unflag-entity-menu-item :flag-entity-menu-item))})
        (zap-toolbar/dropdown-button
         {:icon             :fa-check
          :label            (loc/tr :won)
          :custom-style     "zap-btn__solid__green"
          :dropdown-color   "green"
          :dropdown-action? (not= (:fundraising-opportunity/status entity)
                                  :fundraising-opportunity.status/won)
          :on-select        (fn [_e]
                              (fundraising-opportunity-actions/won! {:entity entity}))})
        (zap-toolbar/dropdown-button
         {:icon             :fa-ban
          :label            (loc/tr :lost)
          :custom-style     "zap-btn__solid__salmon"
          :dropdown-color   "salmon"
          :dropdown-action? (not= (:fundraising-opportunity/status entity)
                                  :fundraising-opportunity.status/lost)
          :on-select        (fn [_e]
                              (modals/open-modal!
                               loss-curtain/modal-key
                               {:fundraising-opportunity-id (:db/id entity)}))})
        (zap-toolbar/dropdown-button
         {:icon             :fa-pause
          :label            (loc/tr :put-on-hold)
          :custom-style     "zap-btn__solid__yellow"
          :dropdown-color   "yellow"
          :dropdown-action? (not= (:fundraising-opportunity/status entity)
                                  :fundraising-opportunity.status/on-hold)
          :on-select        (fn [_e]
                              (modals/open-modal!
                               on-hold-curtain/modal-key
                               {:fundraising-opportunity-id (:db/id entity)}))})
        (zap-toolbar/dropdown-button
         {:icon             :fa-redo-alt
          :label            (loc/tr :activate)
          :custom-style     "zap-btn__solid__blue"
          :dropdown-color   "blue"
          :dropdown-action? (not= (:fundraising-opportunity/status entity)
                                  :fundraising-opportunity.status/active)
          :hidden?          (fn []
                              (not (some #{(:fundraising-opportunity/status entity)}
                                         [:fundraising-opportunity.status/on-hold
                                          :fundraising-opportunity.status/lost])))
          :on-select        (fn []
                              (fundraising-opportunity-actions/resurrect! entity))})])

     (when (and (not (:common/disabled? entity))
                (not (session/is-read-only-user?)))
       [(zap-toolbar/delete-button entity [:fundraising-opportunities-grid])])
     [(nev-components/audit-trail-button {:entity     entity
                                          :entity-ids [entity-id]})])))

(def fees-associated-entities-attributes
  [:common/related-entities-company
   :common/related-entities-person])

(defn header-top
  [{:keys [entity read-only?]}]
  [:div
   {:class "flex items-center gap-2"}
   [:span
    [:i.text-2xl
     {:class (uu/cm :fal (entity/default-icon entity))}]]
   [:div
    [nev-components/name-view
     {:entity     entity
      :read-only? read-only?}]]])

(def default-field-defs
  [{:fields       {0 {:field :fundraising-opportunity/description}
                   1 {:field :fundraising-opportunity/next-steps}}
    :field-layout :f-2-2}])

(defn header-board
  [{:keys [_entity-atom]}]
  (let [header-state (r/atom {:edit-layout? false})
        set-edit-layout! (fn [val] (swap! header-state assoc :edit-layout? val))]
    (fn [{:keys [entity-atom]}]
      (let [entity @entity-atom
            entity-id (:db/id entity)
            field-defs-path [:fundraising-opportunity :header :field-defs]
            field-defs-timeout-id :fundraising-opportunity-field-defs
            field-defs (or (events/timeout-value field-defs-timeout-id
                                                 @(r/track session/get-view-config field-defs-path))
                           default-field-defs)
            overrides nil
            selected-fields (->> field-defs
                                 (map :fields)
                                 (mapcat vals)
                                 (keep :field))
            remove-fields #{}
            schema (templates/entity-type-template
                    {:entity-type     :entity.type/fundraising-opportunity
                     :selected-fields selected-fields
                     :tenant-schema   @session/tenant-schema
                     :account         @session/account
                     :role            @session/role
                     :remove-fields   remove-fields
                     :overrides       overrides})
            ;;TODO later release: add investor fields similar to froppos-grid

            ;;test all fields:
            #_#_field-defs
              (->> schema
                   (map first)
                   (partition-all 4 4)
                   (map (fn [fields]
                          (->> fields
                               (map-indexed (fn [i f]
                                              [i {:field f}])))))
                   (map (partial into {}))
                   (map (fn [fields]
                          {:fields       fields
                           :field-layout :f-1-1-1-1}))
                   vec)]
        [:div
         [header/header-board
          {:top              [header-top {:entity entity}]
           :preview-top      [header-top
                              {:entity     entity
                               :read-only? true}]
           :actions          (toolbar-buttons {:entity entity})
           :entity-id        entity-id
           :entity-atom      entity-atom
           :edit-layout?     (:edit-layout? @header-state)
           :set-edit-layout! set-edit-layout!
           :field-defs       field-defs
           :schema           schema
           :save-layout!     (fn [new-field-defs]
                               (events/save-field-defs! field-defs-timeout-id
                                                        field-defs-path
                                                        new-field-defs
                                                        (fn [])
                                                        (fn [err]
                                                          (logging/error
                                                           "Failed to save field layout"
                                                           err))))}]]))))

(defn new-header?
  []
  (and (session/has-feature-access? :feature/customizable-nev)
       @session/customizable-nev?))

(defn fundraising-opportunity-view
  [{:keys [fundraising-opportunity-id _is-guest?] :as _view-args}]
  (let [state-atom (r/cursor app-db/app-state [:views :nev :fundraising-opportunity])
        initialized? (r/atom false)
        temp-atom (r/cursor state-atom [:temp])
        view-state-atom (r/cursor state-atom [:view-state])
        error-atom (r/cursor state-atom [:error])
        notification-atom (r/cursor state-atom [:notification])
        events-pane-atom (r/cursor state-atom [:events-pane])
        entity-atom (r/cursor session/entities [fundraising-opportunity-id])
        selected-pipeline-id (atom nil)
        select-pipeline! (fn []
                           (when-let [pipeline-id (:fundraising-opportunity/pipeline @entity-atom)]
                             (when (not= pipeline-id @selected-pipeline-id)
                               (reset! selected-pipeline-id pipeline-id)
                               (swap! session/selected-pipelines assoc
                                      :fundraising-opportunity-pipeline pipeline-id))))
        handle-route-change! (partial entity-utils/component-did-update
                                      :fundraising-opportunity-id
                                      (partial events/clear-errors! error-atom notification-atom))]
    (r/create-class
     {:component-did-update
      (fn [this old-argv]
        (handle-route-change! this old-argv)
        (when (= fundraising-opportunity-id (:fundraising-opportunity-id (r/props this)))
          (select-pipeline!)))
      :component-did-mount
      (fn [_]
        (entity-utils/reset-state! state-atom fundraising-opportunity-id
                                   {:always-fetch? (session/new-grid-2023?)})
        (reset! initialized? true)
        (select-pipeline!))
      :component-will-unmount
      (fn [_]
        (events/clear-errors! error-atom notification-atom)
        (entity-utils/clear-state! state-atom))
      :reagent-render
      (fn [{:keys [fundraising-opportunity-id] :as view-args}]
        (let [fundraising-opportunity (get @session/entities fundraising-opportunity-id)
              original-pipeline-id (:fundraising-opportunity/pipeline fundraising-opportunity)
              pipeline (session/get-entity original-pipeline-id)
              header-fields [:fundraising-opportunity/description
                             :fundraising-opportunity/next-steps] ;; TODO: configuration for user
              check-list-fields (->> pipeline
                                     :pipeline/checklist-configurations
                                     (mapcat (fn [checklist-id]
                                               (let [checklist-entity (session/get-entity (or (:db/id checklist-id)
                                                                                              checklist-id))]
                                                 (when (= (:check-list/target-entity-type checklist-entity)
                                                          (:entity/type fundraising-opportunity))
                                                   (->> checklist-entity
                                                        :check-list/configuration)))))
                                     (distinct))]

          (cond
            (not @initialized?)
            [syncing/view]
            (or (:common/disabled? fundraising-opportunity)
                (:not-found @state-atom))
            [not-found/not-found-content]
            (:loading @state-atom)
            [syncing/view]
            :else
            (if (or (not fundraising-opportunity)
                    (:common/disabled? fundraising-opportunity))
              [:div " "]
              (let [tenant-schema @session/tenant-schema
                    view-id :fundraising-opportunity
                    module-collapsed-path (fn [module-id]
                                            [view-id :modules :module-config module-id
                                             :collapsed?])]
                [:<>
                 (when-not (session/is-guest?)
                   [zap-toolbar/nev-toolbar
                    {:state           state-atom
                     :label           (partial zap-toolbar/toolbar-back
                                               [:fundraising-opportunities-grid]
                                               (loc/tr :back-to/fundraising-opportunities-grid))
                     :actions         (toolbar-buttons (assoc view-args
                                                              :entity
                                                              fundraising-opportunity))
                     :dropdown-toggle [:span (loc/tr :actions)
                                       [:i.fas.fa-angle-down.ml-2]]
                     :dropdown-toggle-class-name
                     "zap-button zap-button--primary-btn actions-dropdown"}])
                 [:div.entity__container
                  (when @error-atom
                    [nev-components/error-bar error-atom])
                  (when @notification-atom
                    [nev-components/notification-bar notification-atom])

                  [:div.entity
                   [:div
                    {:class "w-full max-w-[1920px] mx-auto p-4"}
                    [nev-components/owner-stage-rating-module
                     {:entity-cursor            entity-atom
                      :view-state               view-state-atom
                      :entity                   fundraising-opportunity
                      :entity-stage-attribute   :fundraising-opportunity/opportunity-stage
                      :stage-entity-type        :entity.type/opportunity-stage
                      :stage-pipeline-id        (:fundraising-opportunity/pipeline
                                                 fundraising-opportunity)
                      :stage-pipeline-attribute :opportunity-stage/pipeline
                      :ratings-attribute        :fundraising-opportunity/ratings
                      :owner-attribute          :fundraising-opportunity/owner
                      :members-attribute        :common/team-members}]
                    (if (new-header?)
                      [header-board {:entity-atom entity-atom}]
                      [nev-components/header-module
                       {:component-state        temp-atom
                        :related-tags-entity-id (:fundraising-opportunity/investor
                                                 fundraising-opportunity)
                        :view-state             view-state-atom
                        :entity                 fundraising-opportunity
                        :pipeline-id            original-pipeline-id
                        :selected-fields        header-fields}])
                    [:div.md:grid.grid-cols-10.gap-4
                     [:div
                      {:class (if (session/event-pane-disabled?)
                                "col-span-10"
                                "col-span-10 lg:col-span-5")}
                      [:div.entity-left
                       [modules/view
                        {:view-id             view-id
                         :ordering            (get-in @session/view-config [view-id :modules :ordering])
                         :save-modules-order! (partial events/save-modules-order!
                                                       (:entity/type fundraising-opportunity))
                         :modules
                         (concat
                          [{:module-id              :fundraising-opportunity-information
                            :header                 (loc/tr :nev-module/about-this-entity-type
                                                            [(string/lower-case (loc/tr (:entity/type
                                                                                         fundraising-opportunity)))])
                            ;;TODO uncomment when finished:
                            #_:header-tools
                            #_(when (session/is-admin?)
                                [:span.entity-editor-header-settings
                                 {:on-click (fn [e]
                                              ;;open settings
                                              )}
                                 [:span (loc/tr :entity-editor/settings-label)]
                                 [:i.fal.fa-cog]])
                            :collapsed?             (get-in @session/view-config
                                                            (module-collapsed-path
                                                             :fundraising-opportunity-information))
                            :save-module-collapsed! (partial events/save-module-collapsed!
                                                             :entity.type/fundraising-opportunity)
                            :content
                            [:div
                             [entity-editor/view
                              {:state-atom    state-atom
                               :entity-id     fundraising-opportunity-id
                               :view-id       view-id
                               :module-id     :fundraising-opportunity-information
                               :custom-save!  (fn [attribute new-value on-success! on-error!]
                                                (when (= attribute
                                                         :fundraising-opportunity/pipeline)
                                                  (modals/open-modal!
                                                   change-pipeline/modal-key
                                                   {:ofs-opportunity      fundraising-opportunity
                                                    :selected-pipeline-id new-value
                                                    :original-pipeline-id original-pipeline-id
                                                    :on-close!            (on-success!)})
                                                  ;; call pipeline select error! to revert select value
                                                  (on-error!)
                                                  true))
                               :remove-fields (concat [:fundraising-opportunity/opportunity-name
                                                       :fundraising-opportunity/fund-commitment
                                                       :fundraising-opportunity/credit
                                                       :common/team-members
                                                       :fundraising-opportunity/investor
                                                       :common/network-drive :common/named-network-drives]
                                                      (when (tenant-customizations/capman?)
                                                        [:fundraising-opportunity/pipeline])
                                                      (field/entity-tag-fields
                                                       tenant-schema
                                                       (:entity/type fundraising-opportunity))
                                                      header-fields
                                                      check-list-fields)
                               :ordering      (get-in
                                               @session/view-config
                                               [view-id :modules :module-config
                                                :fundraising-opportunity-information :ordering])}]]}
                           (let [module-id :fundraising-investor
                                 order-config-path [view-id :modules :module-config
                                                    module-id :ordering]
                                 fundraising-opportunity-investor (:fundraising-opportunity/investor
                                                                   fundraising-opportunity)
                                 module-label (if fundraising-opportunity-investor
                                                (loc/tr :nev-module/existing-fundraising-investor
                                                        [(loc/tr (->> fundraising-opportunity-investor
                                                                      (get @session/entities)
                                                                      :entity/type))])
                                                (loc/tr :nev-module/fundraising-opportunity-investor))
                                 collapsed-path (module-collapsed-path module-id)
                                 collapsed? (get-in @session/view-config collapsed-path)
                                 header-tools
                                 (when (and fundraising-opportunity-investor
                                            (not collapsed?)
                                            (not (session/is-read-only-user?)))
                                   [:span.grow.flex.justify-end
                                    (when-not (session/is-read-only-user?)
                                      [nev-components/remove-attribute-icon
                                       {:entity    fundraising-opportunity
                                        :attribute :fundraising-opportunity/investor}])])]
                             (nev-components/entity-relation-module
                              {:state-atom             state-atom
                               :module-id              module-id
                               :view-id                view-id
                               :header-fields          [:investor/description]
                               :ordering               (get-in
                                                        @session/view-config
                                                        order-config-path)
                               :order-config-path      order-config-path
                               :module-label           module-label
                               :entity                 fundraising-opportunity
                               :header-tools           header-tools
                               :relationship-attribute :fundraising-opportunity/investor}))
                           (when-let [_commitment (:fundraising-opportunity/fund-commitment
                                                   fundraising-opportunity)]
                             (let [module-id :fundraising-fund-commitment
                                   order-config-path [view-id :modules :module-config
                                                      module-id :ordering]]
                               (nev-components/entity-relation-module
                                {:state-atom        state-atom
                                 :module-id         module-id
                                 :view-id           view-id
                                 :ordering          (get-in
                                                     @session/view-config
                                                     order-config-path)
                                 :order-config-path order-config-path
                                 :module-label      (loc/tr
                                                     :nev-module/fundraising-opportunity-fund-commitment)
                                 :entity            fundraising-opportunity
                                 :relationship-attribute
                                 :fundraising-opportunity/fund-commitment})))]
                          (when-not (session/is-guest?)
                            (associated-entities/modules
                             {:entity-id              fundraising-opportunity-id
                              :save-module-collapsed! events/save-module-collapsed!
                              :module-collapsed-path  module-collapsed-path}))
                          (when (session/fee-tracking?)
                            (fees/modules {:entity-id              fundraising-opportunity-id
                                           :save-module-collapsed! events/save-module-collapsed!
                                           :module-collapsed-path  module-collapsed-path
                                           :fees-associated-entities-attributes
                                           fees-associated-entities-attributes}))
                          (when (not (session/is-guest?))
                            (->> pipeline
                                 :pipeline/checklist-configurations
                                 (keep (fn [checklist-id]
                                         (let [checklist-entity (session/get-entity (or (:db/id checklist-id)
                                                                                        checklist-id))
                                               module-id (keyword (str "check-list-" checklist-id))]
                                           (when (= (:check-list/target-entity-type checklist-entity)
                                                    (:entity/type fundraising-opportunity))
                                             {:module-id              module-id
                                              :header                 (:check-list/label checklist-entity)
                                              :collapsed?             (get-in @session/view-config (module-collapsed-path module-id))
                                              :save-module-collapsed! (partial events/save-module-collapsed! (:entity/type fundraising-opportunity))
                                              :start-collapsed?       true
                                              :header-tools           [check-list-module/header-view tenant-schema fundraising-opportunity-id checklist-id]
                                              :content                [:div
                                                                       [check-list-module/view
                                                                        {:parent-entity-id    fundraising-opportunity-id
                                                                         :checklist-entity-id checklist-id
                                                                         :fields              [{:attribute :role/guest-buy-side
                                                                                                :label     (loc/tr :buy-side-guests)}]}]]}))))))
                          [(when (session/has-feature-access? :feature/entity-esign)
                             (let [module-id :entity-esign]
                               (nev-components/entity-esign-module
                                {:view-id      view-id
                                 :module-id    module-id
                                 :module-label (loc/tr :nev-module/entity-esign)
                                 :entity       fundraising-opportunity})))]
                          [                           ;; removing for now:
                           #_(when (plan/available :feature/buy-side-guest
                                                   @session/tenant
                                                   @session/role)
                               {:module-id              :guest-accounts
                                :header                 (loc/tr :nev-module/guest-accounts)
                                :collapsed?             (get-in @session/view-config
                                                                (module-collapsed-path :guest-accounts))
                                :save-module-collapsed! (partial
                                                         events/save-module-collapsed!
                                                         :entity.type/fundraising-opportunity)
                                :start-collapsed?       (empty? existing-guests)
                                :content                [:div
                                                         [guests-module/view
                                                          {:entity-id          fundraising-opportunity-id
                                                           :fields             [{:attribute
                                                                                 :role/guest-buy-side
                                                                                 :label
                                                                                 (loc/tr :buy-side-guests)}]
                                                           :show-notification! (partial
                                                                                events/show-notification!
                                                                                notification-atom)
                                                           :show-error!        (partial events/show-error!
                                                                                        error-atom)
                                                           :guest-roles        existing-guests}]]})

                           (when (and (not (session/is-mobile?))
                                      (f-permissions/field-read-access?
                                       :common/attachments
                                       :entity.type/fundraising-opportunity
                                       (->> fundraising-opportunity
                                            :fundraising-opportunity/pipeline)))
                             (nev-components/attachments-module view-id
                                                                :fundraising-opportunity-attachments
                                                                fundraising-opportunity))])}]]]
                     (when-not (session/event-pane-disabled?)
                       [:div
                        {:class "col-span-10 lg:col-span-5"}
                        [event-pane/view
                         {:state       events-pane-atom
                          :entity-ids  [fundraising-opportunity-id]
                          :module-args {:state-atom state-atom
                                        :view-id    view-id}}]])]]]]])))))})))

(defmethod routing/render-view :entity.type/fundraising-opportunity
  [{:keys [route-params _view-params]}]
  (let [fundraising-opportunity-id (-> route-params
                                       :id
                                       js/parseInt)]
    [default-page-layout/view
     {:class-name "zap-page fundraising-opportunity"}
     ;;the key change forces rerendering of the component (and its child components)
     ^{:key (str "fundraising-opportunity-viewer-id-" fundraising-opportunity-id)}
     [fundraising-opportunity-view {:fundraising-opportunity-id fundraising-opportunity-id}]]))
